import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import shouldDismissOnOutsideClick from '@cartwhl/ui/hooks/shouldDismissOnOutsideClick';

import {
  ENABLE_DOCKED_MINI_MOTION_PREVIEW,
  ENABLE_MOTION_SELECTION_PERSISTENCE,
  ENABLE_STAGE_MOTION_PREVIEW,
} from './constants';
import { resolveComicMogenJobActors } from './comicMogenJobActors';
import {
  readMotionMiniSelection,
  writeMotionMiniSelection,
} from './motionMiniSelectionStorage';
import { pushCartographerDebugEvent } from '../../lib/cartographerDebug';
import type { AssetLibraryEmbedSelection } from '../../../AssetLibrary';

type PendingActorPickerSelection = {
  motionID: string;
  motionLabel: string | null;
  bodyCount: number;
  bvhURLs: string[];
  mhrBvhURLs: string[];
  mhrIdentityJsonURLs: string[];
};

type MotionPreviewCharacterMappingState = 'saved' | 'unsaved';
export type MotionPreviewSurface = 'mini' | 'stage';

interface UseCartographerMotionWorkflowOptions {
  apiKey: string | null;
  organizationServiceId: string | null;
  loadedCharacterID: string | null;
  activeCharacterFile: File | null;
  hasGltfScene: boolean;
  isCharacterDirty: boolean;
  frontDoorRef: RefObject<HTMLDivElement | null>;
  onClearError: () => void;
}

interface OpenMotionPreviewOptions {
  externalJobId: string;
  externalLabel?: string | null;
  usesUnsavedCharacterMapping?: boolean;
}

export default function useCartographerMotionWorkflow({
  apiKey,
  organizationServiceId,
  loadedCharacterID,
  activeCharacterFile,
  hasGltfScene,
  isCharacterDirty,
  frontDoorRef,
  onClearError,
}: UseCartographerMotionWorkflowOptions) {
  const [motionPreviewOpen, setMotionPreviewOpen] = useState(false);
  const [motionPreviewSession, setMotionPreviewSession] = useState(0);
  const [motionPreviewMeshUrl, setMotionPreviewMeshUrl] = useState<string | null>(
    null,
  );
  const [motionPreviewMeshFile, setMotionPreviewMeshFile] = useState<File | null>(
    null,
  );
  const [motionPreviewMeshFormat, setMotionPreviewMeshFormat] = useState<
    'fbx' | 'glb' | 'gltf' | null
  >(null);
  const [cartographerAssetLibraryMode, setCartographerAssetLibraryMode] =
    useState<'' | 'motions'>('');
  const [motionPreviewExternalJobId, setMotionPreviewExternalJobId] = useState<
    string | null
  >(null);
  const [motionPreviewExternalLabel, setMotionPreviewExternalLabel] = useState<
    string | null
  >(null);
  const [motionPreviewCharacterMappingState, setMotionPreviewCharacterMappingState] =
    useState<MotionPreviewCharacterMappingState>('saved');
  const [selectedMotionId, setSelectedMotionId] = useState<string | null>(null);
  const [selectedMotionLabel, setSelectedMotionLabel] = useState<string | null>(
    null,
  );
  const [selectedBodyIndex, setSelectedBodyIndex] = useState<number | undefined>(
    undefined,
  );
  const [actorPickerSelection, setActorPickerSelection] =
    useState<PendingActorPickerSelection | null>(null);
  const [previewSurface, setPreviewSurface] =
    useState<MotionPreviewSurface | null>(null);
  const [previewOptionsOpen, setPreviewOptionsOpen] = useState(false);
  const [viewMotionUnsavedModalOpen, setViewMotionUnsavedModalOpen] =
    useState(false);

  const canUseMiniPreview = Boolean(
    ENABLE_DOCKED_MINI_MOTION_PREVIEW && hasGltfScene && activeCharacterFile,
  );
  const canUseStagePreview = ENABLE_STAGE_MOTION_PREVIEW && hasGltfScene;
  const defaultPreviewSurface = canUseStagePreview
    ? 'stage'
    : canUseMiniPreview
      ? 'mini'
      : null;

  const stashedPreviewSurfaceRef = useRef<MotionPreviewSurface | null>(null);
  const actorPickerRequestIdRef = useRef(0);

  const resetMotionMiniState = useCallback(() => {
    stashedPreviewSurfaceRef.current = null;
    setSelectedMotionId(null);
    setSelectedMotionLabel(null);
    setSelectedBodyIndex(undefined);
    setActorPickerSelection(null);
    setPreviewSurface(null);
    setPreviewOptionsOpen(false);
  }, []);

  const closeMotionPreview = useCallback(() => {
    setMotionPreviewOpen(false);
    setMotionPreviewMeshFile(null);
    setMotionPreviewMeshFormat(null);
    setMotionPreviewExternalJobId(null);
    setMotionPreviewExternalLabel(null);
    setMotionPreviewMeshUrl((previousUrl) => {
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      return null;
    });
  }, []);

  const openMotionPreview = useCallback(
    (options: OpenMotionPreviewOptions) => {
      if (!apiKey || !loadedCharacterID || !activeCharacterFile) return;

      onClearError();

      const file = activeCharacterFile;
      const extension = file.name.match(/\.(fbx|glb|gltf)$/i)?.[1]?.toLowerCase();
      const meshFormat =
        extension === 'fbx'
          ? 'fbx'
          : extension === 'gltf'
            ? 'gltf'
            : extension === 'glb'
              ? 'glb'
              : null;

      setMotionPreviewMeshFile(file);
      setMotionPreviewMeshFormat(meshFormat);
      setMotionPreviewMeshUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return URL.createObjectURL(file);
      });
      setMotionPreviewSession((session) => session + 1);
      setMotionPreviewExternalJobId(options.externalJobId);
      setMotionPreviewExternalLabel(options.externalLabel ?? null);
      setMotionPreviewCharacterMappingState(
        options.usesUnsavedCharacterMapping ? 'unsaved' : 'saved',
      );
      setMotionPreviewOpen(true);
    },
    [activeCharacterFile, apiKey, loadedCharacterID, onClearError],
  );

  const backFromMotionPreviewToAssetLibrary = useCallback(() => {
    closeMotionPreview();
    setCartographerAssetLibraryMode('motions');
  }, [closeMotionPreview]);

  const applyMotionSelection = useCallback(
    (
      motionID: string,
      motionLabel: string | null,
      bodyIndex?: number,
    ) => {
      setSelectedMotionId(motionID);
      setSelectedMotionLabel(motionLabel);
      setSelectedBodyIndex(bodyIndex);
      setActorPickerSelection(null);
      setPreviewOptionsOpen(true);
      setPreviewSurface((currentSurface) => {
        if (currentSurface === 'stage' && canUseStagePreview) {
          return 'stage';
        }
        if (currentSurface === 'mini' && canUseMiniPreview) {
          return 'mini';
        }
        return defaultPreviewSurface;
      });
      pushCartographerDebugEvent('motionWorkflow.motion.selected', {
        motionID,
        motionLabel,
        bodyIndex,
        loadedCharacterID,
      });

      if (!ENABLE_MOTION_SELECTION_PERSISTENCE || !loadedCharacterID) return;

      writeMotionMiniSelection(organizationServiceId, loadedCharacterID, {
        motionID,
        motionLabel,
        bodyIndex,
      });
    },
    [
      canUseMiniPreview,
      canUseStagePreview,
      defaultPreviewSurface,
      loadedCharacterID,
      organizationServiceId,
    ],
  );

  const handleAssetLibraryPick = useCallback(
    (selection: AssetLibraryEmbedSelection) => {
      const motionID =
        selection.kind === 'mogen-job'
          ? selection.jobId
          : selection.kind === 'stock-motion'
            ? selection.motionId
            : null;
      if (!motionID) return;
      const label = selection.label;

      setCartographerAssetLibraryMode('');
      setMotionPreviewCharacterMappingState(isCharacterDirty ? 'unsaved' : 'saved');

      if (selection.kind !== 'mogen-job' || !apiKey) {
        applyMotionSelection(motionID, label);
        return;
      }

      const requestId = ++actorPickerRequestIdRef.current;

      void (async () => {
        const preview = await resolveComicMogenJobActors(apiKey, motionID);
        if (actorPickerRequestIdRef.current !== requestId) {
          return;
        }

        if (preview.bodyCount <= 1) {
          applyMotionSelection(motionID, label);
          return;
        }

        setActorPickerSelection({
          motionID,
          motionLabel: label,
          bodyCount: preview.bodyCount,
          bvhURLs: preview.bvhURLs,
          mhrBvhURLs: preview.mhrBvhURLs,
          mhrIdentityJsonURLs: preview.mhrIdentityJsonURLs,
        });
      })();
    },
    [apiKey, applyMotionSelection, isCharacterDirty],
  );

  const handleActorPickerOpenChange = useCallback((open: boolean) => {
    if (!open) {
      actorPickerRequestIdRef.current += 1;
      setActorPickerSelection(null);
    }
  }, []);

  const handleActorPickerSelect = useCallback(
    (bodyIndex: number) => {
      if (!actorPickerSelection) return;
      applyMotionSelection(
        actorPickerSelection.motionID,
        actorPickerSelection.motionLabel,
        bodyIndex,
      );
    },
    [actorPickerSelection, applyMotionSelection],
  );

  const handleOpenFullMotionPreviewFromMini = useCallback(() => {
    if (!selectedMotionId) return;

    openMotionPreview({
      externalJobId: selectedMotionId,
      externalLabel: selectedMotionLabel,
      usesUnsavedCharacterMapping:
        motionPreviewCharacterMappingState === 'unsaved',
    });
  }, [
    motionPreviewCharacterMappingState,
    openMotionPreview,
    selectedMotionId,
    selectedMotionLabel,
  ]);

  const setPreviewSurfaceWithFallback = useCallback(
    (nextSurface: MotionPreviewSurface | null) => {
      let resolvedSurface = nextSurface;
      if (nextSurface === null) {
        resolvedSurface = null;
      } else if (nextSurface === 'mini') {
        resolvedSurface = canUseMiniPreview
          ? 'mini'
          : canUseStagePreview
            ? 'stage'
            : null;
      } else if (nextSurface === 'stage') {
        resolvedSurface = canUseStagePreview
          ? 'stage'
          : canUseMiniPreview
            ? 'mini'
            : null;
      }

      pushCartographerDebugEvent('motionWorkflow.previewSurface.request', {
        requestedSurface: nextSurface,
        resolvedSurface,
        selectedMotionId,
        canUseMiniPreview,
        canUseStagePreview,
        hasGltfScene,
        hasActiveCharacterFile: Boolean(activeCharacterFile),
      });
      setPreviewSurface(resolvedSurface);
    },
    [
      activeCharacterFile,
      canUseMiniPreview,
      canUseStagePreview,
      hasGltfScene,
      selectedMotionId,
    ],
  );

  const rememberAndClearPreviewSurface = useCallback(() => {
    if (previewSurface !== null) {
      stashedPreviewSurfaceRef.current = previewSurface;
    }
    setPreviewSurfaceWithFallback(null);
  }, [previewSurface, setPreviewSurfaceWithFallback]);

  const toggleToolDockMotionPreview = useCallback(() => {
    if (!selectedMotionId) return;

    if (previewSurface !== null) {
      rememberAndClearPreviewSurface();
      setPreviewOptionsOpen(false);
      pushCartographerDebugEvent('motionWorkflow.preview.masterToggle', {
        action: 'off',
        stashedSurface: stashedPreviewSurfaceRef.current,
        selectedMotionId,
      });
      return;
    }

    const restore =
      stashedPreviewSurfaceRef.current ?? defaultPreviewSurface;
    if (restore === null) return;

    setPreviewSurfaceWithFallback(restore);
    setPreviewOptionsOpen(true);
    pushCartographerDebugEvent('motionWorkflow.preview.masterToggle', {
      action: 'on',
      restoredSurface: restore,
      selectedMotionId,
    });
  }, [
    defaultPreviewSurface,
    previewSurface,
    rememberAndClearPreviewSurface,
    selectedMotionId,
    setPreviewSurfaceWithFallback,
  ]);

  const activateUnsavedMotionPreview = useCallback(() => {
    if (!isCharacterDirty) return false;
    setMotionPreviewCharacterMappingState('unsaved');
    return true;
  }, [isCharacterDirty]);

  const openAssetLibrary = useCallback(() => {
    setCartographerAssetLibraryMode('motions');
  }, []);

  const requestOpenAssetLibrary = useCallback(() => {
    if (isCharacterDirty) {
      setViewMotionUnsavedModalOpen(true);
      return;
    }

    openAssetLibrary();
  }, [isCharacterDirty, openAssetLibrary]);

  const closeAssetLibrary = useCallback(() => {
    setCartographerAssetLibraryMode('');
  }, []);

  useEffect(() => {
    if (!ENABLE_DOCKED_MINI_MOTION_PREVIEW) {
      setSelectedMotionId(null);
      setSelectedMotionLabel(null);
      setSelectedBodyIndex(undefined);
      setPreviewSurface(null);
      setPreviewOptionsOpen(false);
      return;
    }

    if (!loadedCharacterID) {
      setSelectedMotionId(null);
      setSelectedMotionLabel(null);
      setSelectedBodyIndex(undefined);
      setPreviewSurface(null);
      setPreviewOptionsOpen(false);
      return;
    }

    // Keep the in-memory selection when persistence is off.
    if (!ENABLE_MOTION_SELECTION_PERSISTENCE) {
      return;
    }

    const persistedSelection = readMotionMiniSelection(
      organizationServiceId,
      loadedCharacterID,
    );
    if (!persistedSelection) {
      setSelectedMotionId(null);
      setSelectedMotionLabel(null);
      setSelectedBodyIndex(undefined);
      setPreviewSurface(null);
      setPreviewOptionsOpen(false);
      return;
    }

    setSelectedMotionId(persistedSelection.motionID);
    setSelectedMotionLabel(persistedSelection.motionLabel);
    setSelectedBodyIndex(persistedSelection.bodyIndex);
    setPreviewSurface(defaultPreviewSurface);
    setPreviewOptionsOpen(true);
  }, [
    defaultPreviewSurface,
    loadedCharacterID,
    organizationServiceId,
  ]);

  useEffect(() => {
    if (previewSurface === 'mini' && !canUseMiniPreview) {
      setPreviewSurface(selectedMotionId && canUseStagePreview ? 'stage' : null);
      return;
    }
    if (previewSurface === 'stage' && !canUseStagePreview) {
      setPreviewSurface(selectedMotionId && canUseMiniPreview ? 'mini' : null);
    }
  }, [canUseMiniPreview, canUseStagePreview, previewSurface, selectedMotionId]);

  useEffect(() => {
    pushCartographerDebugEvent('motionWorkflow.state', {
      selectedMotionId,
      selectedMotionLabel,
      selectedBodyIndex,
      previewSurface,
      previewOptionsOpen,
      motionPreviewOpen,
      motionPreviewCharacterMappingState,
      cartographerAssetLibraryMode,
      canUseMiniPreview,
      canUseStagePreview,
      loadedCharacterID,
    });
  }, [
    canUseMiniPreview,
    canUseStagePreview,
    cartographerAssetLibraryMode,
    loadedCharacterID,
    motionPreviewCharacterMappingState,
    motionPreviewOpen,
    selectedBodyIndex,
    previewOptionsOpen,
    previewSurface,
    selectedMotionId,
    selectedMotionLabel,
  ]);

  useClickOutside(frontDoorRef, () => {
    if (cartographerAssetLibraryMode !== 'motions') {
      return;
    }

    setCartographerAssetLibraryMode('');
  }, {
    shouldTrigger: shouldDismissOnOutsideClick,
  });

  useEffect(() => {
    if (isCharacterDirty && hasGltfScene) return;
    setViewMotionUnsavedModalOpen(false);
  }, [hasGltfScene, isCharacterDirty]);

  useEffect(() => {
    if (loadedCharacterID && isCharacterDirty) return;
    setMotionPreviewCharacterMappingState('saved');
  }, [isCharacterDirty, loadedCharacterID]);

  useEffect(() => {
    return () => {
      if (motionPreviewMeshUrl) {
        URL.revokeObjectURL(motionPreviewMeshUrl);
      }
    };
  }, [motionPreviewMeshUrl]);

  const toggleMotionMini = useCallback(() => {
    if (!selectedMotionId || !ENABLE_DOCKED_MINI_MOTION_PREVIEW) return;
    setPreviewOptionsOpen(true);
    setPreviewSurfaceWithFallback(previewSurface === 'mini' ? null : 'mini');
  }, [previewSurface, selectedMotionId, setPreviewSurfaceWithFallback]);

  const motionMiniToggleDisabled =
    !ENABLE_DOCKED_MINI_MOTION_PREVIEW ||
    !hasGltfScene ||
    !activeCharacterFile ||
    !selectedMotionId;
  const motionMiniToggleLabel = previewSurface === 'mini'
    ? 'Hide mini motion preview'
    : 'Show mini motion preview';

  return {
    activateUnsavedMotionPreview,
    actorPickerSelection,
    backFromMotionPreviewToAssetLibrary,
    cartographerAssetLibraryMode,
    closeAssetLibrary,
    closeMotionPreview,
    handleActorPickerOpenChange,
    handleActorPickerSelect,
    handleAssetLibraryPick,
    handleOpenFullMotionPreviewFromMini,
    motionMiniToggleDisabled,
    motionMiniToggleLabel,
    motionPreviewCharacterMappingState,
    motionPreviewExternalJobId,
    motionPreviewExternalLabel,
    motionPreviewMeshFile,
    motionPreviewMeshFormat,
    motionPreviewMeshUrl,
    motionPreviewOpen,
    motionPreviewSession,
    openAssetLibrary,
    openMotionPreview,
    previewOptionsOpen,
    previewSurface,
    requestOpenAssetLibrary,
    resetMotionMiniState,
    selectedBodyIndex,
    selectedMotionId,
    selectedMotionLabel,
    setPreviewSurface: setPreviewSurfaceWithFallback,
    setViewMotionUnsavedModalOpen,
    rememberAndClearPreviewSurface,
    toggleMotionMini,
    toggleToolDockMotionPreview,
    viewMotionUnsavedModalOpen,
  };
}
